import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Search,
  Loader2,
  Users,
  Phone,
  Mail,
  RefreshCw,
  StickyNote,
  ShoppingBag,
  Calendar,
  Trash2,
  Eye,
  Receipt,
  Clock,
  DollarSign,
  Star,
  Gift,
  Award,
  TrendingUp,
  Minus,
  Tag,
  X,
  Check,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function Customers() {
  const { t } = useTranslation();
  const { api, user: currentUser } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Loyalty state
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [loyaltyRewards, setLoyaltyRewards] = useState([]);
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [pointsAdjustment, setPointsAdjustment] = useState({ points: '', reason: '' });
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  
  // Tags state
  const [availableTags, setAvailableTags] = useState([]);
  const [filterTag, setFilterTag] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    tags: [],
  });

  const canManage = ['OWNER', 'ADMIN', 'MANAGER'].includes(currentUser?.role);

  const tagColors = {
    gray: 'bg-gray-500/10 text-gray-700 border-gray-500/30',
    blue: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
    green: 'bg-green-500/10 text-green-700 border-green-500/30',
    yellow: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30',
    red: 'bg-red-500/10 text-red-700 border-red-500/30',
    purple: 'bg-purple-500/10 text-purple-700 border-purple-500/30',
    orange: 'bg-orange-500/10 text-orange-700 border-orange-500/30',
  };

  const fetchTags = useCallback(async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/customer-tags', { params: { outlet_id: currentOutlet.id } });
      setAvailableTags(resp.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  }, [api, currentOutlet?.id]);

  const fetchCustomers = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      const resp = await api.get('/customers', {
        params: { outlet_id: currentOutlet.id, search: searchQuery || undefined, limit: 200 }
      });
      setCustomers(resp.data);
    } catch (error) {
      toast.error('Gagal memuat data pelanggan');
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id, searchQuery]);

  useEffect(() => {
    fetchCustomers();
    fetchTags();
  }, [fetchCustomers, fetchTags]);

  const fetchCustomerOrders = useCallback(async (customerId) => {
    if (!currentOutlet?.id) return;
    setLoadingOrders(true);
    try {
      const resp = await api.get('/orders', {
        params: { outlet_id: currentOutlet.id }
      });
      const filtered = resp.data.filter(order => order.customer_id === customerId);
      setCustomerOrders(filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setCustomerOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [api, currentOutlet?.id]);

  const fetchLoyaltyData = useCallback(async (customerId) => {
    setLoadingLoyalty(true);
    try {
      const resp = await api.get(`/customers/${customerId}/loyalty`);
      setLoyaltyData(resp.data);
      setLoyaltyRewards(resp.data.available_rewards || []);
    } catch (error) {
      console.error('Failed to fetch loyalty data:', error);
      setLoyaltyData(null);
      setLoyaltyRewards([]);
    } finally {
      setLoadingLoyalty(false);
    }
  }, [api]);

  const handleAdjustPoints = async () => {
    if (!selectedCustomer || !pointsAdjustment.points || !pointsAdjustment.reason.trim()) {
      toast.error('Poin dan alasan wajib diisi');
      return;
    }
    try {
      const resp = await api.post(`/customers/${selectedCustomer.id}/loyalty/adjust`, null, {
        params: { 
          points: parseInt(pointsAdjustment.points), 
          reason: pointsAdjustment.reason 
        }
      });
      toast.success(resp.data.message);
      setShowPointsDialog(false);
      setPointsAdjustment({ points: '', reason: '' });
      fetchLoyaltyData(selectedCustomer.id);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyesuaikan poin');
    }
  };

  const handleRedeemReward = async () => {
    if (!selectedCustomer || !selectedReward) return;
    try {
      const resp = await api.post(`/customers/${selectedCustomer.id}/loyalty/redeem`, null, {
        params: { reward_id: selectedReward.id }
      });
      toast.success(resp.data.message);
      setShowRedeemDialog(false);
      setSelectedReward(null);
      fetchLoyaltyData(selectedCustomer.id);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menukar poin');
    }
  };

  const handleToggleTag = async (customerId, tagId, currentTags = []) => {
    try {
      if (currentTags.includes(tagId)) {
        await api.delete(`/customers/${customerId}/tags/${tagId}`);
      } else {
        await api.post(`/customers/${customerId}/tags`, null, { params: { tag_id: tagId } });
      }
      fetchCustomers();
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(prev => ({
          ...prev,
          tags: currentTags.includes(tagId) 
            ? prev.tags.filter(t => t !== tagId)
            : [...(prev.tags || []), tagId]
        }));
      }
    } catch (error) {
      toast.error('Gagal mengubah tag');
    }
  };

  const getTagById = (tagId) => availableTags.find(t => t.id === tagId);

  const openDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        notes: customer.notes || '',
        tags: customer.tags || [],
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        notes: '',
        tags: [],
      });
    }
    setShowDialog(true);
  };

  const openDetailSheet = async (customer) => {
    setSelectedCustomer(customer);
    setShowDetailSheet(true);
    setLoyaltyData(null);
    await Promise.all([
      fetchCustomerOrders(customer.id),
      fetchLoyaltyData(customer.id)
    ]);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nama pelanggan wajib diisi');
      return;
    }

    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData);
        toast.success('Data pelanggan berhasil diperbarui');
      } else {
        await api.post('/customers', {
          ...formData,
          outlet_id: currentOutlet.id,
        });
        toast.success('Pelanggan baru berhasil ditambahkan');
      }
      setShowDialog(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data pelanggan');
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Hapus pelanggan "${customer.name}"? Data order yang terkait tetap tersimpan.`)) {
      return;
    }
    try {
      await api.delete(`/customers/${customer.id}`);
      toast.success('Pelanggan berhasil dihapus');
      fetchCustomers();
      if (showDetailSheet && selectedCustomer?.id === customer.id) {
        setShowDetailSheet(false);
      }
    } catch (error) {
      toast.error('Gagal menghapus pelanggan');
    }
  };

  const handleUpdateNotes = async (notes) => {
    if (!selectedCustomer) return;
    try {
      await api.put(`/customers/${selectedCustomer.id}`, { notes });
      setSelectedCustomer({ ...selectedCustomer, notes });
      toast.success('Catatan berhasil disimpan');
      fetchCustomers();
    } catch (error) {
      toast.error('Gagal menyimpan catatan');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
      CONFIRMED: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      PREPARING: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
      READY: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
      SERVED: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
      COMPLETED: 'bg-green-500/10 text-green-600 border-green-500/30',
      CANCELLED: 'bg-red-500/10 text-red-600 border-red-500/30',
      VOID: 'bg-gray-500/10 text-gray-600 border-gray-500/30',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 border-gray-500/30';
  };

  // Filter customers by tag
  const filteredCustomers = filterTag 
    ? customers.filter(c => c.tags?.includes(filterTag))
    : customers;

  return (
    <div className="p-6 space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('customers.title')}</h1>
          <p className="text-muted-foreground">{t('customers.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchCustomers(); fetchTags(); }} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          {canManage && (
            <Button onClick={() => openDialog()} data-testid="add-customer-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('customers.add_customer')}
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('customers.search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="customer-search"
        />
      </div>

      {/* Tag Filters */}
      {availableTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Tag className="h-4 w-4" />
            {t('common.filter')}:
          </span>
          <Button
            variant={filterTag === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterTag(null)}
          >
            {t('common.all')}
          </Button>
          {availableTags.map((tag) => (
            <Button
              key={tag.id}
              variant={filterTag === tag.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterTag(filterTag === tag.id ? null : tag.id)}
              className={filterTag !== tag.id ? tagColors[tag.color] || tagColors.gray : ''}
            >
              {tag.name}
              {tag.discount_percent > 0 && (
                <span className="ml-1 text-xs">({tag.discount_percent}%)</span>
              )}
            </Button>
          ))}
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('customers.total_customers')}</p>
                <p className="text-2xl font-bold">{filteredCustomers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customers Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchQuery || filterTag ? t('empty_states.no_customers') : t('empty_states.no_customers')}
            </p>
            {canManage && !searchQuery && !filterTag && (
              <Button onClick={() => openDialog()} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                {t('customers.add_customer')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.map((customer) => (
            <Card 
              key={customer.id} 
              className={`cursor-pointer hover:border-primary/50 transition-colors ${!customer.is_active ? 'opacity-60' : ''}`}
              onClick={() => openDetailSheet(customer)}
              data-testid={`customer-card-${customer.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold flex-shrink-0">
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{customer.name}</h3>
                        {customer.tags?.map(tagId => {
                          const tag = getTagById(tagId);
                          return tag ? (
                            <Badge key={tagId} variant="outline" className={`text-xs ${tagColors[tag.color] || tagColors.gray}`}>
                              {tag.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                      {customer.phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                        </p>
                      )}
                      {customer.email && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3" />
                          {customer.email}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          <ShoppingBag className="h-3 w-3 mr-1" />
                          {customer.total_orders || 0} order
                        </Badge>
                        {(customer.loyalty_points > 0 || customer.lifetime_points > 0) && (
                          <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-700 border-yellow-500/30">
                            <Star className="h-3 w-3 mr-1" />
                            {customer.loyalty_points || 0} poin
                          </Badge>
                        )}
                        {customer.last_visit && (
                          <Badge variant="outline" className="text-xs">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(customer.last_visit)}
                          </Badge>
                        )}
                      </div>
                      {customer.notes && (
                        <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
                          <StickyNote className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{customer.notes}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDetailSheet(customer)}
                      title="Lihat Detail"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {canManage && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDialog(customer)}
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(customer)}
                          title="Hapus"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCustomer ? t('customers.edit_customer') : t('customers.add_customer_new')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('customers.name_required')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('customers.customer_name_placeholder')}
                data-testid="customer-name-input"
              />
            </div>
            <div>
              <Label>{t('customers.phone_number')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="08xxxxxxxxxx"
                data-testid="customer-phone-input"
              />
              <p className="text-xs text-muted-foreground mt-1">{t('customers.phone_unique')}</p>
            </div>
            <div>
              <Label>{t('customers.email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('customers.email_optional')}
                data-testid="customer-email-input"
              />
            </div>
            {canManage && (
              <div>
                <Label>{t('customers.internal_notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('customers.notes_not_printed')}
                  rows={3}
                  data-testid="customer-notes-input"
                />
                <p className="text-xs text-muted-foreground mt-1">{t('customers.staff_only_notes')}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} data-testid="save-customer-btn">
              {editingCustomer ? t('customers.save_changes') : t('customers.add_customer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{t('customers.customer_details')}</SheetTitle>
          </SheetHeader>
          
          {selectedCustomer && (
            <ScrollArea className="h-[calc(100vh-100px)] mt-4">
              <div className="space-y-6 pr-4">
                {/* Customer Info */}
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xl font-bold flex-shrink-0">
                    {selectedCustomer.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold">{selectedCustomer.name}</h2>
                    {selectedCustomer.phone && (
                      <p className="text-muted-foreground flex items-center gap-2 mt-1">
                        <Phone className="h-4 w-4" />
                        {selectedCustomer.phone}
                      </p>
                    )}
                    {selectedCustomer.email && (
                      <p className="text-muted-foreground flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {selectedCustomer.email}
                      </p>
                    )}
                    {/* Customer Tags */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedCustomer.tags?.map(tagId => {
                        const tag = getTagById(tagId);
                        return tag ? (
                          <Badge key={tagId} variant="outline" className={`${tagColors[tag.color] || tagColors.gray}`}>
                            {tag.name}
                            {tag.discount_percent > 0 && ` (${tag.discount_percent}%)`}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                  {canManage && (
                    <Button variant="outline" size="sm" onClick={() => openDialog(selectedCustomer)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>

                {/* Tag Management */}
                {availableTags.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <Tag className="h-4 w-4" />
                      Kelola Tag
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {availableTags.map((tag) => {
                        const isActive = selectedCustomer.tags?.includes(tag.id);
                        return (
                          <Button
                            key={tag.id}
                            variant={isActive ? 'default' : 'outline'}
                            size="sm"
                            className={!isActive ? tagColors[tag.color] || tagColors.gray : ''}
                            onClick={() => handleToggleTag(selectedCustomer.id, tag.id, selectedCustomer.tags || [])}
                          >
                            {isActive ? <Check className="h-3 w-3 mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                            {tag.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="p-3 text-center">
                      <ShoppingBag className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                      <p className="text-lg font-bold">{selectedCustomer.total_orders || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Order</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <DollarSign className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                      <p className="text-lg font-bold">{formatCurrency(selectedCustomer.total_spent)}</p>
                      <p className="text-xs text-muted-foreground">Total Belanja</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Clock className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                      <p className="text-sm font-medium">{formatDate(selectedCustomer.last_visit)}</p>
                      <p className="text-xs text-muted-foreground">Kunjungan Terakhir</p>
                    </CardContent>
                  </Card>
                </div>

                <Separator />

                {/* Loyalty Points Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      Loyalty Points
                    </h3>
                    {canManage && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setPointsAdjustment({ points: '', reason: '' });
                          setShowPointsDialog(true);
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Sesuaikan Poin
                      </Button>
                    )}
                  </div>
                  
                  {loadingLoyalty ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Points Balance */}
                      <div className="grid grid-cols-2 gap-3">
                        <Card className="bg-yellow-500/10 border-yellow-500/30">
                          <CardContent className="p-3 text-center">
                            <Star className="h-5 w-5 mx-auto text-yellow-600 mb-1" />
                            <p className="text-2xl font-bold text-yellow-700">{loyaltyData?.current_points || selectedCustomer.loyalty_points || 0}</p>
                            <p className="text-xs text-yellow-600">Poin Saat Ini</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-3 text-center">
                            <TrendingUp className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                            <p className="text-lg font-bold">{loyaltyData?.lifetime_points || selectedCustomer.lifetime_points || 0}</p>
                            <p className="text-xs text-muted-foreground">Total Poin Diperoleh</p>
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* Available Rewards */}
                      {loyaltyRewards.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-1">
                            <Gift className="h-4 w-4" />
                            Hadiah Tersedia
                          </p>
                          <div className="space-y-2">
                            {loyaltyRewards.slice(0, 3).map((reward) => {
                              const canRedeem = (loyaltyData?.current_points || 0) >= reward.points_required;
                              return (
                                <div 
                                  key={reward.id} 
                                  className={`flex items-center justify-between p-2 rounded-md border ${canRedeem ? 'bg-green-500/5 border-green-500/30' : 'bg-muted/50'}`}
                                >
                                  <div>
                                    <p className="text-sm font-medium">{reward.name}</p>
                                    <p className="text-xs text-muted-foreground">{reward.points_required} poin</p>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    variant={canRedeem ? 'default' : 'outline'}
                                    disabled={!canRedeem}
                                    onClick={() => {
                                      setSelectedReward(reward);
                                      setShowRedeemDialog(true);
                                    }}
                                    className={canRedeem ? 'bg-green-600 hover:bg-green-700' : ''}
                                  >
                                    <Award className="h-3 w-3 mr-1" />
                                    Tukar
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Recent Transactions */}
                      {loyaltyData?.transactions?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Riwayat Poin Terbaru</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {loyaltyData.transactions.slice(0, 5).map((txn) => (
                              <div key={txn.id} className="flex items-center justify-between text-xs p-1.5 bg-muted/50 rounded">
                                <span className="text-muted-foreground truncate max-w-[60%]">{txn.notes}</span>
                                <span className={`font-mono font-medium ${txn.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {txn.points > 0 ? '+' : ''}{txn.points}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Internal Notes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <StickyNote className="h-4 w-4" />
                      Catatan Internal
                    </Label>
                  </div>
                  {canManage ? (
                    <div className="space-y-2">
                      <Textarea
                        value={selectedCustomer.notes || ''}
                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, notes: e.target.value })}
                        placeholder="Tambahkan catatan untuk staff..."
                        rows={3}
                        data-testid="detail-notes-input"
                      />
                      <Button 
                        size="sm" 
                        onClick={() => handleUpdateNotes(selectedCustomer.notes)}
                        data-testid="save-notes-btn"
                      >
                        Simpan Catatan
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
                      {selectedCustomer.notes || 'Tidak ada catatan'}
                    </p>
                  )}
                </div>

                <Separator />

                {/* Order History */}
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <Receipt className="h-4 w-4" />
                    Riwayat Order
                  </h3>
                  
                  {loadingOrders ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : customerOrders.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Belum ada riwayat order
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {customerOrders.slice(0, 20).map((order) => (
                        <Card key={order.id} className="overflow-hidden">
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-medium">#{order.order_number}</span>
                                <Badge variant="outline" className={getStatusColor(order.status)}>
                                  {order.status}
                                </Badge>
                              </div>
                              <span className="font-semibold">{formatCurrency(order.total)}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>{formatDateTime(order.created_at)}</span>
                              <span>{order.items?.length || 0} item</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {customerOrders.length > 20 && (
                        <p className="text-xs text-muted-foreground text-center py-2">
                          Menampilkan 20 order terakhir dari {customerOrders.length} total
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>

      {/* Adjust Points Dialog */}
      <Dialog open={showPointsDialog} onOpenChange={setShowPointsDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              {t('customers.adjust_points')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('customers.points_amount')}</Label>
              <Input
                type="number"
                value={pointsAdjustment.points}
                onChange={(e) => setPointsAdjustment({ ...pointsAdjustment, points: e.target.value })}
                placeholder={t('loyalty.points_adjustment_hint')}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('loyalty.current_balance')}: {loyaltyData?.current_points || selectedCustomer?.loyalty_points || 0} {t('loyalty.points_unit')}
              </p>
            </div>
            <div>
              <Label>{t('customers.reason_required')}</Label>
              <Textarea
                value={pointsAdjustment.reason}
                onChange={(e) => setPointsAdjustment({ ...pointsAdjustment, reason: e.target.value })}
                placeholder={t('loyalty.adjustment_reason_placeholder')}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPointsDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAdjustPoints}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem Reward Dialog */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-green-600" />
              {t('loyalty.redeem_reward')}
            </DialogTitle>
          </DialogHeader>
          {selectedReward && (
            <div className="space-y-4">
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-4 text-center">
                  <Award className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <h3 className="font-bold text-lg">{selectedReward.name}</h3>
                  {selectedReward.description && (
                    <p className="text-sm text-muted-foreground">{selectedReward.description}</p>
                  )}
                  <p className="text-xl font-bold text-green-700 mt-2">{selectedReward.points_required} {t('loyalty.points_unit')}</p>
                </CardContent>
              </Card>
              <div className="text-sm text-center">
                <p>{t('customers.balance_after_redemption')}</p>
                <p className="font-bold text-lg">
                  {(loyaltyData?.current_points || 0) - selectedReward.points_required} {t('loyalty.points_unit')}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeemDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleRedeemReward} className="bg-green-600 hover:bg-green-700">
              <Award className="h-4 w-4 mr-2" />
              {t('loyalty.confirm_redeem')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
